import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { useCallback, useMemo, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Grid from '@mui/material/Unstable_Grid2';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
// _mock
import { USER_STATUS_OPTIONS } from 'src/_mock';
// assets
import { countries } from 'src/assets/data';
// components
import Iconify from 'src/components/iconify';
import { useSnackbar } from 'src/components/snackbar';
import { useResponsive } from 'src/hooks/use-responsive';
import FormProvider, { RHFSelect, RHFTextField, RHFAutocomplete } from 'src/components/hook-form';
import { DatePicker } from '@mui/x-date-pickers';
import axiosInstance from 'src/api/axiosInstance';
import { MenuItem } from '@mui/material';
import { parseISO } from 'date-fns';
import { getCurrentProject, getCurrentServer, getMandEndPoint } from 'src/helper/server-address';
import UpperCaseTextField from 'src/components/hook-form/uppercase-text-field';
import useNavigateErrorHandler from 'src/hooks/useNavigateErrorHandler';
import TitleCasedTextField from 'src/components/hook-form/titlecase-text-field';
import { fetchEncryptedToken } from 'src/utils/encryptUtils';
// ----------------------------------------------------------------------

export default function MstQuickEditForm({ currentUser, open, onClose, getArrData, tableData }) {
  const token = sessionStorage.getItem('token');
  const { enqueueSnackbar } = useSnackbar();
  const [selectedUser, setSelectedUser] = useState({});
  const handleApiError = useNavigateErrorHandler();
  const [country, setCountry] = useState([]);
  const [state, setState] = useState([]);
  const [city, setCity] = useState([]);
  const [statesAvailable, setStatesAvailable] = useState(false);
  const [citiesAvailable, setCitiesAvailable] = useState(false);

  // ----------My code------------------------- //

  const NewProductSchema = Yup.object().shape({
    name: Yup.string()
      .required('Subscriber Site is required')
      .max(50, 'Subscriber Site cannot be more than 50 characters')
      .test(
        'no-special-characters',
        'Subscriber Site can only contain letters, numbers, spaces, and hyphens. Example: "Hydrabad"',
        (value) => /^[a-zA-Z0-9\s-]+$/.test(value)
      ),

    country: Yup.mixed().required('Country is required'),

    ...(statesAvailable && {
      state: Yup.mixed().required('State is required'),
    }),
    ...(citiesAvailable && {
      city: Yup.mixed().required('City is required'),
    }),


    startDate: Yup.date()
      .typeError('Start date must be a valid date')
      .required('Start date is required'),
    endDate: Yup.date()
      .nullable()
      .typeError('End date must be a valid date')
      .min(Yup.ref('startDate'), '"End Date" should be greater than "Start Date"'),
  });



  const defaultValues = useMemo(
    () => ({
      sid: currentUser?.id,
      name: currentUser?.mst || '',
      country: currentUser?.country || null,
      state: currentUser?.state || null,
      city: currentUser?.city || null,
      startDate: currentUser ? new Date(currentUser.start) : new Date(), // Set the previous start date if editing, or a new date if creating 
      endDate: currentUser?.end ? new Date(currentUser.end) : null, // Initialize with an empty string

      subscrSiteId: 1,
      createdBy: 1,
      createdDate: new Date(),
      lastUpdatedBy: 1,
      lastUpdatedDate: new Date(),
    }),
    [currentUser]
  );



  const methods = useForm({
    resolver: yupResolver(NewProductSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
    trigger,
  } = methods;

  const values = watch();

  useEffect(() => {
    if (currentUser) {
      reset(defaultValues);
    }
  }, [currentUser, defaultValues, reset]);

  const getCountriesData = () => {
    axiosInstance
      .get(
        `/rest/subscrsite/countries`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        setCountry(res.data);
      })
      .catch(() => {
        setCountry([]);
        handleApiError();
      });
  };


  const handleCountryChange = (countryId) => {
    setCity([]);
    setCitiesAvailable(false);
    axiosInstance
      .get(
        `/rest/subscrsite/states?countryId=${countryId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        setState(res.data);
        setStatesAvailable(res.data.length > 0);
      })
      .catch(() => {
        setState([]);
        setStatesAvailable(false);
        handleApiError();
      });
  };

  const handleStateChange = (stateId) => {
    axiosInstance
      .get(
        `/rest/subscrsite/cities?stateId=${stateId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((res) => {
        setCity(res.data);
        setCitiesAvailable(res.data.length > 0);
      })
      .catch(() => {
        setCity([]);
        setCitiesAvailable(false);
        handleApiError();
      });
  };

  useEffect(() => {
    getCountriesData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Separate handler for update operation
  const handleUpdateSubscrSite = async (data) => {
    const user = JSON.parse(localStorage.getItem('user'));

    const encryptedId = await fetchEncryptedToken(data.sid, token);
    if (!encryptedId) throw new Error('Failed to encrypt ID');

    try {
      const Data = {
        subscrSiteId: currentUser?.id,
        subscrSiteName: data.name,
        subscrCountry: data.country,
        subscrState: data.state,
        subscrCity: data.city,
        startDate: data.startDate,
        endDate: data.endDate,
        lastUpdatedDate: new Date(),
        
      };



      const url = `rest/subscrsite/updt/${encryptedId}`
      const res = await axiosInstance.put(url, Data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.data) {
        // Null response indicates duplicate record
        enqueueSnackbar('Duplicate record detected. Please make necessary modifications.', {
          variant: 'error',
        });
        return; // Stop further execution if duplicate
      }

      enqueueSnackbar('Subscriber Site Updated Successfully!');
      getArrData();  // Refresh the data
      onClose();
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Handle duplicate record error
        enqueueSnackbar('Duplicate record detected. Please make necessary modifications.', {
          variant: 'error',
        });
      } else {
        handleApiError(error);
        // Handle other errors
        enqueueSnackbar('An error occurred. Please try again later.', { variant: 'error' });
      }
    }
  };

  // Separate handler for create operation
  const handleCreateSubscrSite = async (data) => {
    const user = JSON.parse(localStorage.getItem('user'));
    try {

      const nameToCheck = data.name.trim().toLowerCase();
      const existing = tableData.find((item) => item.mst.trim().toLowerCase() === nameToCheck);

      if (existing) {
        if (existing.status === 'Inactive') {
          enqueueSnackbar('This record already exists in Inactive. You can restore it.', {
            variant: 'warning',
          });
          return;
        }
        if (existing.status === 'Active') {
          const error = new Error('Duplicate record');
          error.response = { status: 409 };
          throw error;
        }
      }

      const Data = {
        subscrSiteId: currentUser?.id,
        subscrSiteName: data.name,
        startDate: data.startDate,
        subscrCountry: data.country,
        subscrState: data.state,
        subscrCity: data.city,
        endDate: data.endDate,
        createdAt: new Date(),
        lastUpdatedAt: new Date(),
         
        
      };

      const url = `/rest/subscrsite/add`;
      await axiosInstance.post(url, Data, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      reset();  // Reset form fields
      onClose();
      getArrData();  // Refresh the data
      setState([]);
      setCity([]);
      enqueueSnackbar('Subscriber Site Created Successfully!');
    } catch (error) {
      if (error.response && error.response.status === 409) {
        // Handle duplicate record error
        enqueueSnackbar('Duplicate record detected. Please make necessary modifications.', {
          variant: 'error',
        });
      } else {
        handleApiError(error);
        // Handle other errors
        enqueueSnackbar('An error occurred. Please try again later.', { variant: 'error' });
      }
    }
  };

  // The onSubmit function is now more concise
  const onSubmit = handleSubmit(async (data) => {
    try {
      data = {
        ...data,
        name: data.name.trim().replace(/\s+/g, ' ')
      }
      if (currentUser) {
        // If the user exists (update operation)
        await handleUpdateSubscrSite(data);
      } else {
        // If the user doesn't exist (create operation)
        await handleCreateSubscrSite(data);
      }

      // Refresh data and close the form after the operation
      await getArrData();
    } catch (error) {
      enqueueSnackbar('An error occurred. Please try again later.', { variant: 'error' });
      handleApiError(error);
    }
  });

  let stateTooltipTitle = '';
  if (!watch('country')) {
    stateTooltipTitle = 'Please select a country first';
  } else if (state.length === 0) {
    stateTooltipTitle = 'States are not available.';
  }

  let cityTooltipTitle = '';
  if (!watch('state')) {
    cityTooltipTitle = 'Please select a state first';
  } else if (city.length === 0) {
    cityTooltipTitle = 'Cities are not available.';
  }


  return (
    <Dialog
      fullWidth
      maxWidth={false}
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: 720, marginTop: '-200px' },
      }}
    >
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <DialogTitle>{currentUser ? 'Edit Subscriber Site ' : 'Add Subscriber Site'}</DialogTitle>

        <DialogContent>
          <Box display="grid" md={2}>
            <TitleCasedTextField
              name="name"
              label={
                <div>
                  SubScriber Site <span style={{ color: 'red', fontWeight: 'bolder' }}>*</span>
                </div>
              }
              style={{ marginTop: '10px' }}
            />
          </Box>
          <br />

          {/* Country, State & City in One Row */}
          <Box display="grid" rowGap={2} columnGap={2} gridTemplateColumns={{ xs: "1fr", sm: "repeat(3, 1fr)" }}>
            <RHFAutocomplete
              name="country"
              label={
                <div>
                  Country <span style={{ color: "red", fontWeight: "bolder" }}>*</span>
                </div>
              }
              placeholder="Select Country"
              options={country}
              disableClearable
              fullWidth
              onChange={(event, value) => {
                setValue("country", value, { shouldValidate: true });
                setValue("state", null, { shouldValidate: false });
                setValue("city", null, { shouldValidate: false });
                handleCountryChange(value.countriesId);
              }}
              getOptionLabel={(option) => option.countryName}
            />

            <RHFAutocomplete
              name="state"
              label={
                <div>
                  State {statesAvailable && <span style={{ color: "red", fontWeight: "bolder" }}>*</span>}
                </div>
              }
              placeholder="Select State"
              options={state}
              disableClearable
              fullWidth
              onChange={(event, value) => {
                setValue("state", value, { shouldValidate: true });
                setValue("city", null, { shouldValidate: false });
                handleStateChange(value.statesId);
              }}
              getOptionLabel={(option) => option.stateName}
            />

            <RHFAutocomplete
              name="city"
              label={
                <div>
                  City {citiesAvailable && <span style={{ color: "red", fontWeight: "bolder" }}>*</span>}
                </div>
              }
              placeholder="Select City"
              options={city}
              disableClearable
              fullWidth
              getOptionLabel={(option) => option.cityName}
            />
          </Box>
          <br />
          {/* Start Date & End Date in Last Row */}
          <Box
            rowGap={3}
            columnGap={2}
            display="grid"
            style={{ marginTop: '10px' }}
            gridTemplateColumns={{
              xs: 'repeat(1, 1fr)',
              sm: 'repeat(2, 1fr)',
            }}
          >

            <Controller
              name="startDate"
              control={control}
              render={({ field, fieldState: { error: fieldError } }) => {
                try {
                  // Convert ZonedDateTime to JavaScript Date object
                  const startDate = field.value ? parseISO(field.value) : null;
                  return (
                    <DatePicker
                      label={
                        <div>
                          Start date <span style={{ color: 'red', fontWeight: 'bold' }}>*</span>
                        </div>
                      }
                      value={field.value}
                      onChange={(newValue) => {
                        field.onChange(newValue);
                        trigger('startDate');
                        trigger('endDate');
                      }}
                     
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!fieldError,
                          helperText: fieldError?.message,
                        },
                      }}
                    />
                  );
                } catch (error) {
                  return null;
                }
              }}
            />

            {selectedUser?.endDate !== null ? ( // Conditionally render DatePicker
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => {
                  try {
                    // Convert ZonedDateTime to JavaScript Date object
                    const endDate = field.value ? parseISO(field.value) : null;
                    return (
                      <DatePicker
                        label="End date"
                        value={field.value}
                        onChange={(newValue) => {
                          field.onChange(newValue);
                          trigger('endDate');
                        }}
                       
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            error: !!fieldError,
                            helperText: fieldError?.message,
                          },
                        }}
                      />
                    );
                  } catch (error) {
                    return null;
                  }
                }}
              />
            ) : (
              // Render a regular text field when endDate is null or empty
              <Controller
                name="endDate"
                control={control}
                render={({ field, fieldState: { error: fieldError } }) => (
                  <DatePicker
                    label="End date"
                    value={field.value}
                    onChange={(newValue) => {
                      field.onChange(newValue);
                      trigger('endDate');
                    }}
                   
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!fieldError,
                        helperText: fieldError?.message,
                      },
                    }}
                  />
                )}
              />
            )}

          </Box>
        </DialogContent>

        <DialogActions>
          <Button variant="soft" color="error" onClick={() => {
            reset(); // This will reset the form to its default values
            onClose(); // Close the dialog after resetting the form
            getArrData();
          }}>
            Cancel
          </Button>

          <LoadingButton type="submit" variant="soft" color="success" loading={isSubmitting}>
            {!currentUser ? 'Create Subscriber Site ' : 'Save Changes'}
          </LoadingButton>
        </DialogActions>
      </FormProvider>
    </Dialog>
  );
}

MstQuickEditForm.propTypes = {
  currentUser: PropTypes.object,
  onClose: PropTypes.func,
  open: PropTypes.bool,
  getArrData: PropTypes.func,
  tableData: PropTypes.array,
};
